"use client";

import React from "react";
import { useToastStore, ToastType } from "@/lib/stores/useToastStore";

function ToastIcon({ type }: { type: ToastType }) {
  if (type === "loading") {
    return (
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        style={{
          animation: "ds-spin 0.8s linear infinite",
          fill: "none",
          stroke: "var(--ds-primary, #3D7AFF)",
          strokeWidth: 2.2,
          flexShrink: 0,
        }}
      >
        <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
        <path d="M12 3a9 9 0 019 9" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "success") {
    return (
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 999,
          background: "rgba(18, 136, 62, 0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          style={{
            fill: "none",
            stroke: "var(--ds-up, #12883E)",
            strokeWidth: 2.5,
            strokeLinecap: "round",
            strokeLinejoin: "round",
          }}
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
    );
  }
  if (type === "error") {
    return (
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 999,
          background: "rgba(220, 38, 38, 0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          style={{
            fill: "none",
            stroke: "var(--ds-down, #DC2626)",
            strokeWidth: 2.5,
            strokeLinecap: "round",
            strokeLinejoin: "round",
          }}
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </div>
    );
  }
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: 999,
        background: "rgba(61, 122, 255, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="13"
        height="13"
        style={{
          fill: "none",
          stroke: "var(--ds-primary, #3D7AFF)",
          strokeWidth: 2.5,
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }}
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8h.01M12 12v4" />
      </svg>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: "fixed",
        top: 70,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 380,
        width: "calc(100vw - 32px)",
        pointerEvents: "none",
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            pointerEvents: "auto",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "10px 14px",
            background: "var(--ds-surface, #ffffff)",
            color: "var(--ds-text, #0f172a)",
            border: "1px solid var(--ds-line, #e5e7eb)",
            borderRadius: 10,
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
            fontSize: 13,
            lineHeight: 1.45,
            transition: "all 0.2s ease",
            animation: "ds-toast-slide-in 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <ToastIcon type={t.type} />
          <div style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
            {t.title && (
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, color: "var(--ds-text, #0f172a)" }}>
                {t.title}
              </div>
            )}
            <div style={{ color: "var(--ds-text-2, #475569)", fontSize: 12.5 }}>
              {t.message}
            </div>
          </div>
          <button
            type="button"
            onClick={() => removeToast(t.id)}
            aria-label="Close notification"
            style={{
              background: "none",
              border: "none",
              padding: 2,
              cursor: "pointer",
              color: "var(--ds-text-3, #94a3b8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 4,
              flexShrink: 0,
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" style={{ fill: "none", stroke: "currentColor", strokeWidth: 2 }}>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
      <style>{`
        @keyframes ds-toast-slide-in {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
